import {
  AppConfig,
  useAppConstantsContext,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { isUserInPhoneOnlyTest, useScreenSize } from '@ltvco/refresh-lib/utils';
import { SearchData, SearchTabData, useAccount } from '@ltvco/refresh-lib/v1';
import {
  FeatureBlockedModal,
  useFeatureBlockedModal,
  useSearchFormContext,
} from '@ltvco/refresh-lib/v2';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DesktopSearchHub } from './Desktop';
import { MobileSearchHub } from './Mobile';
import { decorateSearchTabs, getUnclaimedMoneyTab } from './utils';

export const SearchHub = ({
  shouldDisplayUcmAddonModal,
}: {
  shouldDisplayUcmAddonModal?: boolean;
}) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const { isMobile } = useScreenSize();
  const { useSearchForm } = useSearchFormContext();
  const { searchTabs, activeFormTab, setActiveFormTab } = useSearchForm(true);
  const navigate = useNavigate();

  const [searchType, setSearchType] = useState<string>('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [filteredSearchTabs, setFilteredSearchTabs] = useState<SearchTabData[]>(
    []
  );
  const { data: account } = useAccount(true);
  const { isBlocked, isHidden, phoneOnlyVariation } =
    useSubscriptionAccessConfigContext();
  const { phoneOnlyPlanNames } = useAppConstantsContext();
  const isPhoneOnlyVariation =
    isUserInPhoneOnlyTest(account, phoneOnlyPlanNames) || phoneOnlyVariation;
  const {
    isFeatureBlockedModalOpen,
    openFeatureBlockedModal,
    closeFeatureBlockedModal,
  } = useFeatureBlockedModal();

  const checkIfFeatureIsBlocked = (tabDisplayName: string) => {
    if (tabDisplayName === 'People') {
      return isBlocked('person');
    }
    return isBlocked(tabDisplayName.toLowerCase());
  };

  useEffect(() => {
    let visibleTabs = searchTabs;

    const unclaimedMoneyTab = getUnclaimedMoneyTab();
    if (isPhoneOnlyVariation || isHidden('unclaimed_money')) {
      visibleTabs = visibleTabs.filter(
        (tab) => tab.id !== unclaimedMoneyTab.id
      );
    } else {
      const hasUnclaimedMoney = visibleTabs.some(
        (tab) => tab.id === unclaimedMoneyTab.id
      );
      visibleTabs = hasUnclaimedMoney
        ? visibleTabs
        : [...visibleTabs, unclaimedMoneyTab];
    }

    visibleTabs = visibleTabs.filter((tab) => !tab.href);

    const filtered = visibleTabs.filter(
      (tab) =>
        !isHidden(
          tab.displayName === 'People'
            ? 'person'
            : tab.displayName.toLowerCase()
        )
    );

    const decoratedSearchTabs = decorateSearchTabs(filtered);

    setFilteredSearchTabs(decoratedSearchTabs);
  }, [
    searchTabs,
    isHidden,
    isPhoneOnlyVariation,
    account,
    phoneOnlyPlanNames,
    phoneOnlyVariation,
  ]);

  useEffect(() => {
    if (filteredSearchTabs.length > 0) {
      const firstUnblockedTab =
        filteredSearchTabs.find(
          (tab) => !checkIfFeatureIsBlocked(tab.displayName)
        ) ?? filteredSearchTabs[0];
      setActiveFormTab(firstUnblockedTab.id);
    }
  }, [filteredSearchTabs]);

  const handleTabClick = (_event: React.SyntheticEvent, tabId: string) => {
    if (tabId === 'unclaimedMoneyTab' && shouldDisplayUcmAddonModal) {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'cta_dashboard_search',
        subtype: 'ucm_upsell',
      });

      setSearchType('Unclaimed Money');
      openFeatureBlockedModal();
      return;
    }

    const tab = filteredSearchTabs.find((t) => t.id === tabId);
    trackEventGA4({
      event_name: 'navigation_click',
      search_by: tabId,
      interacted_from: 'dashboard',
    });

    if (checkIfFeatureIsBlocked(tab.displayName)) {
      setSearchType(tab.displayName);
      openFeatureBlockedModal();
      return;
    }

    if (tab?.href) {
      navigate(tab.href);
    }

    setActiveFormTab(tabId);
  };

  const handleSearchSuccess = ({
    reportType,
    searchParams,
    permalink,
  }: SearchData) => {
    const url = `/report/${reportType}?${searchParams}&permalink=${permalink}`;
    navigate(url);
  };

  return (
    <>
      {isMobile ? (
        <MobileSearchHub
          onSearchSuccess={handleSearchSuccess}
          placement="dashboard"
          searchTabs={filteredSearchTabs}
          activeTab={activeFormTab}
          onTabChange={handleTabClick}
        />
      ) : (
        <DesktopSearchHub
          onSearchSuccess={handleSearchSuccess}
          placement="dashboard"
          searchTabs={filteredSearchTabs}
          activeTab={activeFormTab}
          onTabChange={handleTabClick}
        />
      )}

      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </>
  );
};
